// src/pages/AddDriver.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createDriver } from '../api/drivers';
import useVehicles from '../hooks/useVehicles';

export default function AddDriver() {
  const navigate = useNavigate();
  const { vehicles, loading } = useVehicles();

  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    phone: '',
    address: {
      line_1: '',
      line_2: '',
      town: '',
      county: '',
      postal_code: '',
      country: 'UK',
    },
    role: 'DRIVER',
    vehicle: '',
  });

  const [errors, setErrors] = useState({});
  const [genericError, setGenericError] = useState('');
  const [saving, setSaving] = useState(false);

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const updateAddress = (field, value) =>
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));

  const clearFieldError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const submit = async () => {
    setSaving(true);
    setErrors({});
    setGenericError('');
    try {
      await createDriver(form);
      navigate('/drivers');
    } catch (err) {
      console.error('Failed to save driver:', err);
      if (err.status === 400 && err.validationErrors) {
        // Validation errors in shape { field: "message", ... }
        setErrors(err.validationErrors || {});
      } else {
        setGenericError(
          'A server error occurred while saving the driver. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Add New Driver
      </Typography>

      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Enter the driver’s personal, contact and address information.
      </Typography>

      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGenericError('')}
        >
          {genericError}
        </Alert>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          mx: 'auto',
        }}
      >
        {/* PERSONAL INFORMATION */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Personal Information
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            <TextField
              label="First Name"
              fullWidth
              value={form.firstname}
              onChange={(e) => {
                updateField('firstname', e.target.value);
                clearFieldError('firstname');
              }}
              error={!!errors.firstname}
              helperText={errors.firstname}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 6}}>
            <TextField
              label="Last Name"
              fullWidth
              value={form.lastname}
              onChange={(e) => {
                updateField('lastname', e.target.value);
                clearFieldError('lastname');
              }}
              error={!!errors.lastname}
              helperText={errors.lastname}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* CONTACT DETAILS */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Contact Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            <TextField
              label="Phone Number"
              fullWidth
              value={form.phone}
              onChange={(e) => {
                updateField('phone', e.target.value);
                clearFieldError('phone');
              }}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ADDRESS */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Address
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12}}>
            <TextField
              label="Address Line 1"
              fullWidth
              value={form.address.line_1}
              onChange={(e) => updateAddress('line_1', e.target.value)}
              // if backend ever validates address.line_1, you can map it here
              error={!!errors['address.line_1']}
              helperText={errors['address.line_1']}
            />
          </Grid>

          <Grid item size={{xs: 12}}>
            <TextField
              label="Address Line 2 (optional)"
              fullWidth
              value={form.address.line_2}
              onChange={(e) => updateAddress('line_2', e.target.value)}
              error={!!errors['address.line_2']}
              helperText={errors['address.line_2']}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Town"
              fullWidth
              value={form.address.town}
              onChange={(e) => updateAddress('town', e.target.value)}
              error={!!errors['address.town']}
              helperText={errors['address.town']}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="County"
              fullWidth
              value={form.address.county}
              onChange={(e) => updateAddress('county', e.target.value)}
              error={!!errors['address.county']}
              helperText={errors['address.county']}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Postal Code"
              fullWidth
              value={form.address.postal_code}
              onChange={(e) => updateAddress('postal_code', e.target.value)}
              error={!!errors['address.postal_code']}
              helperText={errors['address.postal_code']}
            />
          </Grid>

          <Grid item size={{xs: 12, sm: 4}}>
            <TextField
              label="Country"
              fullWidth
              value={form.address.country}
              onChange={(e) => updateAddress('country', e.target.value)}
              error={!!errors['address.country']}
              helperText={errors['address.country']}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* VEHICLE ASSIGNMENT */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Vehicle Assignment
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            <TextField
              select
              label="Assign Vehicle (optional)"
              fullWidth
              SelectProps={{ native: false }}
              value={form.vehicle}
              onChange={(e) => {
                updateField('vehicle', e.target.value);
                clearFieldError('vehicle');
              }}
              error={!!errors.vehicle}
              helperText={errors.vehicle}
              disabled={loading}
            >
              <MenuItem value="">
                <em>No Vehicle Assigned</em>
              </MenuItem>

              {vehicles.map((v) => (
                <MenuItem key={v._id || v.id} value={v._id || v.id}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{v.vrm}</Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {v.make_model || v.make}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* ACTION BUTTONS */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/drivers')}
            sx={{ textTransform: 'none' }}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={submit}
            sx={{ textTransform: 'none' }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Driver'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
